import React, { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { InputRow, DetailRow } from "../../components/Rows";
import { FormCard } from "../FormCard";
import OcpiModulesList from "./OcpiModulesList";
import { withTranslatedLabels } from "../../i18n/options";

const BASE_OCPI_MODULES = [
    { label: "Cdrs", value: "cdrs" },
    { label: "Commands", value: "commands" },
    { label: "Credentials", value: "credentials" },
    { label: "Locations", value: "locations" },
    { label: "Sessions", value: "sessions" },
    { label: "Tariffs", value: "tariffs" },
    { label: "Tokens", value: "tokens" },
    { label: "Booking", value: "Booking" },
];

const PartyFormDetails = ({ party, handleInputChange, moduleIdentifiers, onModuleIdentifiersChange }) => {
    const { t } = useTranslation();
    const isOcpi221 = party.versionNumber === "2.2.1";

    const ocpiModules = useMemo(() => {
        const modules = isOcpi221
            ? BASE_OCPI_MODULES.filter((m) => m.value !== "Booking")
            : BASE_OCPI_MODULES;
        return withTranslatedLabels(modules, t, "ocpiParty.module");
    }, [isOcpi221, t]);

    const handleModuleIdentifiersChange = useCallback(
        (next) => {
            if (isOcpi221) {
                onModuleIdentifiersChange(next.filter((id) => id !== "Booking"));
                return;
            }
            onModuleIdentifiersChange(next);
        },
        [isOcpi221, onModuleIdentifiersChange]
    );

    const knownValues = new Set(ocpiModules.map((m) => m.value));
    const extraFromParty = (party.modules || [])
        .map((m) => m.identifier)
        .filter((id) => id && !knownValues.has(id) && !(isOcpi221 && id === "Booking"));
    const extraFromSelection = (moduleIdentifiers || []).filter(
        (id) => id && !knownValues.has(id) && !(isOcpi221 && id === "Booking")
    );
    const extraModules = [...new Set([...extraFromParty, ...extraFromSelection])].map((id) => ({
        label: id,
        value: id,
    }));
    const allModuleOptions = [...ocpiModules, ...extraModules];

    return (
        <>
            <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-x-4">
                <InputRow
                    label={t("common.name")}
                    value={party.remotePartyName || ""}
                    onChange={(v) => handleInputChange("remotePartyName", v)}
                />

                <DetailRow label={t("form.versionNumber")} value={party.versionNumber} />

                <DetailRow label={t("entity.customer")} value={party.customerName} />

                <DetailRow label={t("form.tokenADecoded")} value={party.tokenA} />
                <DetailRow label={t("form.tokenAEncoded")} value={party.encodedTokenA} />

                <DetailRow label={t("form.tokenBDecoded")} value={party.tokenB} />
                <DetailRow label={t("form.tokenBEncoded")} value={party.encodedTokenB} />

                <DetailRow label={t("form.tokenCDecoded")} value={party.tokenC} />
                <DetailRow label={t("form.tokenCEncoded")} value={party.encodedTokenC} />

                <DetailRow
                    label={t("form.handshakeCompleted")}
                    value={party.isOcpiHandshakeCompleted ? t("common.yes") : t("common.no")}
                />
                <DetailRow
                    label={t("form.ocpiUrl")}
                    value={`${process.env.REACT_APP_GITO_API_OCPI_URL}`}
                />
            </div>

            <div className="mt-4">
                <FormCard
                    title={t("form.roles")}
                    className="!p-5 shadow-md"
                    titleClassName="text-lg"
                    description={t("ocpiParty.rolesDescription")}
                >
                    {party.roles && party.roles.length > 0 ? (
                        <div className="flex flex-col gap-4">
                            {party.roles.map((role, index) => (
                                <div
                                    key={`${role.roleName}-${role.countryCode}-${role.partyId}-${index}`}
                                    className="grid sm:grid-cols-1 md:grid-cols-2 gap-x-4 border-b border-mute-100 pb-4 last:border-0 last:pb-0"
                                >
                                    <DetailRow label={t("common.role")} value={role.roleName} />
                                    <DetailRow label={t("common.country")} value={role.countryCode} />
                                    <DetailRow label={t("form.partyId")} value={role.partyId} />
                                    <DetailRow label={t("form.businessName")} value={role.businessDetailName} />
                                    <DetailRow label={t("common.website")} value={role.website} />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-sm text-mute-600">{t("ocpiParty.noRoles")}</div>
                    )}
                </FormCard>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4 items-start">
                <div className="min-w-0">
                    <FormCard
                        title={t("form.ocpiModules")}
                        className="!p-5 shadow-md h-full"
                        titleClassName="text-lg"
                        description={t("ocpiParty.modulesSaveHint")}
                    >
                        <OcpiModulesList
                            modules={allModuleOptions}
                            selected={moduleIdentifiers || []}
                            onChange={handleModuleIdentifiersChange}
                        />
                    </FormCard>
                </div>

                <div className="min-w-0">
                    <FormCard
                        title={t("form.endpoints")}
                        className="!p-5 shadow-md h-full"
                        titleClassName="text-lg"
                        description={t("ocpiParty.endpointsDescription")}
                    >
                        {party.remotePartyEndpoints && party.remotePartyEndpoints.length > 0 ? (
                            <div className="flex flex-col gap-4">
                                {party.remotePartyEndpoints
                                    .slice()
                                    .sort((a, b) => a.identifier.localeCompare(b.identifier))
                                    .map((ep, index) => (
                                        <div
                                            key={index}
                                            className="border-b border-mute-100 pb-4 last:border-0 last:pb-0"
                                        >
                                            <div className="text-sm font-semibold text-mute-700">
                                                {ep.identifier.toUpperCase()}
                                            </div>
                                            <div className="text-sm text-mute-600 break-all mt-1 leading-normal">
                                                {ep.url}
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        ) : (
                            <div className="text-sm text-mute-600">{t("ocpiParty.noEndpoints")}</div>
                        )}
                    </FormCard>
                </div>
            </div>



        </>
    );
};

export default PartyFormDetails;
